import { Component, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule } from '@angular/router';
import {
  KcPersonaSwitchComponent,
  KcBrowseLaneComponent,
  KcContentCardComponent,
  KcSearchBarComponent,
  KcCitation,
  KcPersona,
  KcProductLine,
} from '../ui';
import { KnowledgeService } from '../../../core/services/api/knowledge.service';
import { KnowledgeBrowseItem } from '../../../core/models/learn/knowledge-browse-item';
import { KnowledgeSearchOverlayComponent } from '../knowledge-search/knowledge-search-overlay.component';
import { KnowledgeSeoService } from '../../../core/services/seo/knowledge-seo.service';
import { environment } from '../../../../environments/environment';

/** A suggested question → grounded answer. Curated static content (Phase 2 has no AI backend). */
interface QA {
  chip: string;
  q: string;
  a: string;
  citations: KcCitation[];
}

/**
 * Knowledge Centre hub (#46) — the front door. Composes the feature-local kc-* components:
 * a persona segmented control, a search bar, suggested-question chips, an on-demand AI answer
 * card, and two browse lanes (Learn + Help & FAQ) populated from the live API browse endpoint.
 *
 * Persona re-scopes everything: browse items are filtered client-side by audience (an item shows
 * when its audience matches the persona OR is null/empty → general content for both), the suggested
 * questions swap, and any open answer is cleared.
 *
 * The AI answer is illustrative only (Phase 2 — no backend yet): the chips surface CURATED static
 * answers ported from the validated prototype, and the answer card keeps the "demo" provenance.
 */
@Component({
  selector: 'app-knowledge-hub',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    KcPersonaSwitchComponent,
    KcBrowseLaneComponent,
    KcContentCardComponent,
    KcSearchBarComponent,
    KnowledgeSearchOverlayComponent,
  ],
  template: `
    <div class="tw-bg-white tw-min-h-screen tw-pb-16 tw-font-body">
      <!-- ============ HERO ============ -->
      <section
        class="tw-text-center tw-py-[44px] tw-px-6"
        style="background:radial-gradient(900px 360px at 50% 70px, #fbecea 0%, #f8f9fb 72%);"
      >
        <div class="tw-max-w-[1080px] tw-mx-auto">
          <div class="tw-flex tw-justify-center tw-mb-[24px]">
            <kc-persona-switch
              [persona]="persona"
              (personaChange)="setPersona($event)"
            ></kc-persona-switch>
          </div>

          <h1 class="tw-text-[34px] tw-leading-[1.1] tw-m-0 tw-text-[#1a1a1a] tw-font-bold tw-font-heading">
            What do you want to know?
          </h1>

          <!-- AI-first search bar (#47): the feature-local kc-search-bar is the hero
               call-to-action — a styled readonly affordance. It does NOT take typed input;
               clicking it (or "Search instead" / "Ask AI") OPENS the blended-search modal
               overlay, where the real typeable input lives. -->
          <div class="tw-max-w-[680px] tw-mx-auto tw-mt-[30px] tw-mb-[14px]">
            <kc-search-bar
              [persona]="persona"
              (searchClick)="openSearch()"
              (aiClick)="openSearch()"
            ></kc-search-bar>
          </div>

          <!-- suggested-question chips: open the overlay pre-filled with the example query -->
          <div class="tw-flex tw-flex-wrap tw-gap-[10px] tw-justify-center tw-mt-[16px]">
            <button
              *ngFor="let item of qas"
              type="button"
              (click)="openSearch(item.q)"
              [class]="chipClasses(item)"
            >
              {{ item.chip }}
            </button>
          </div>

          <div class="tw-flex tw-gap-[6px] tw-items-center tw-justify-center tw-text-[12px] tw-text-[#8a8a8a] tw-mt-[18px]">
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" class="tw-flex-shrink-0"><path d="M12 2 4 5v6c0 5 3.4 8.5 8 11 4.6-2.5 8-6 8-11V5z"/></svg>
            Regulated by the Securities Commission Malaysia · not financial advice
          </div>
        </div>
      </section>

      <!-- ============ BROWSE LANES (real CMS items — always visible; #46 layout) ============ -->
      <section class="tw-max-w-[1080px] tw-mx-auto tw-px-6 tw-mt-12">
        <h2 class="tw-text-[20px] tw-m-0 tw-mb-1 tw-font-semibold tw-text-[#1a1a1a] tw-font-heading">
          Browse topics
        </h2>

        <!-- loading -->
        <div *ngIf="loading" class="tw-text-[14px] tw-text-[#9a9fa6] tw-py-10 tw-text-center">
          Loading…
        </div>

        <!-- empty / error (browse() degrades to [] when the CMS is down) -->
        <div
          *ngIf="!loading && !learnItems.length && !helpItems.length"
          class="tw-text-[14px] tw-text-[#9a9fa6] tw-py-10 tw-text-center"
        >
          Nothing to browse just yet. Please check back soon.
        </div>

        <ng-container *ngIf="!loading">
          <kc-browse-lane *ngIf="learnItems.length" lane="learn" subtitle="understand how it works">
            <a
              *ngFor="let item of learnItems"
              class="tw-no-underline tw-block tw-relative"
              [routerLink]="['/learn/read', item.slug]"
            >
              <!-- Gated marker: rendered here (not projected into the card) because
                   kc-content-card only exposes the [kc-icon] slot — a default-slot
                   badge silently vanishes. Deeper "card badge slot" fix tracked in #69. -->
              <span *ngIf="item.gated" class="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-text-[10.5px] tw-font-semibold tw-uppercase tw-tracking-[.04em] tw-text-[#9a6a00] tw-bg-[#fff4d6] tw-rounded-full tw-px-2 tw-py-[2px]">Members</span>
              <kc-content-card
                lane="learn"
                [title]="item.title"
                [description]="cardDescription(item)"
                [productLine]="toProductLine(item.productLine)"
              >
                <svg kc-icon width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 5a2 2 0 0 1 2-2h12v18H6a2 2 0 0 1-2-2z"/><path d="M8 7h7M8 11h7"/></svg>
              </kc-content-card>
            </a>
          </kc-browse-lane>

          <kc-browse-lane *ngIf="helpItems.length" lane="help" subtitle="get a specific task done">
            <a
              *ngFor="let item of helpItems"
              class="tw-no-underline tw-block tw-relative"
              [routerLink]="['/learn/faq', item.slug]"
            >
              <span *ngIf="item.gated" class="tw-absolute tw-top-3 tw-right-3 tw-z-10 tw-text-[10.5px] tw-font-semibold tw-uppercase tw-tracking-[.04em] tw-text-[#9a6a00] tw-bg-[#fff4d6] tw-rounded-full tw-px-2 tw-py-[2px]">Members</span>
              <kc-content-card
                lane="help"
                [title]="item.title"
                [description]="cardDescription(item)"
                [productLine]="toProductLine(item.productLine)"
              >
                <svg kc-icon width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H8l-5 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg>
              </kc-content-card>
            </a>
          </kc-browse-lane>
        </ng-container>
      </section>

      <!-- ============ BLENDED SEARCH OVERLAY (#47 — opened by the hero bar / chips) ============ -->
      <app-knowledge-search-overlay
        [open]="searchOpen"
        [persona]="persona"
        [initialQuery]="searchSeed"
        (close)="closeSearch()"
      ></app-knowledge-search-overlay>
    </div>
  `,
})
export class KnowledgeHubComponent implements OnInit, OnDestroy {
  persona: KcPersona = 'investor';

  loading = true;
  private items: KnowledgeBrowseItem[] = [];
  learnItems: KnowledgeBrowseItem[] = [];
  helpItems: KnowledgeBrowseItem[] = [];

  // ----- blended search overlay (#47) -----
  /** Whether the blended-search modal overlay is open. */
  searchOpen = false;
  /** Query to pre-fill the overlay with when it opens (e.g. a suggested-question chip). */
  searchSeed = '';

  constructor(
    private knowledge: KnowledgeService,
    private seo: KnowledgeSeoService,
  ) {}

  ngOnInit(): void {
    // #51 SEO: site-level Organization + WebSite/SearchAction JSON-LD and a self-referencing
    // canonical for the hub front door — server-rendered into <head> (present in view-source).
    this.seo.setHeadJsonLd(this.seo.buildSiteLd(), 'site');
    const hubUrl = `${(environment.baseUrl || '').replace(/\/+$/, '')}/learn/hub`;
    this.seo.setCanonicalAndHreflang(hubUrl, 'en');
    this.load();
  }

  ngOnDestroy(): void {
    this.seo.clearHeadJsonLd('site');
    this.seo.clearManagedLinks();
  }

  private load(): void {
    this.loading = true;
    this.knowledge.browse().subscribe({
      next: (items) => {
        this.items = items || [];
        this.deriveLanes();
        this.loading = false;
      },
      // API degrades to [] when the CMS is down; never crash — show the empty state.
      error: () => {
        this.items = [];
        this.deriveLanes();
        this.loading = false;
      },
    });
  }

  /** Persona re-scopes: keep items whose audience matches the persona, plus general (null/empty). */
  private deriveLanes(): void {
    const scoped = this.items.filter((i) => !i.audience || i.audience === this.persona);
    this.learnItems = scoped.filter((i) => i.type === 'learn');
    this.helpItems = scoped.filter((i) => i.type === 'faq');
  }

  setPersona(p: KcPersona): void {
    this.persona = p;
    this.deriveLanes();
    // The overlay re-ranks its active query off the [persona] input binding; nothing else to do.
  }

  /**
   * Opens the blended-search modal overlay. The hero bar + chips call this; an optional `seed`
   * (a suggested question) pre-fills the modal's input and runs the search on open. Re-seeds on
   * each open so a chip click always reflects the chosen question.
   */
  openSearch(seed = ''): void {
    this.searchSeed = (seed || '').trim();
    this.searchOpen = true;
  }

  /** Closes the overlay (Esc / backdrop / X) and clears the seed so the next open starts clean. */
  closeSearch(): void {
    this.searchOpen = false;
    this.searchSeed = '';
  }

  chipClasses(_item: QA): string {
    return 'tw-rounded-full tw-px-[14px] tw-py-[8px] tw-text-[13px] tw-cursor-pointer tw-border tw-transition-colors tw-bg-white tw-border-[#e7e7e7] tw-text-[#5a5a5a] hover:tw-border-primary hover:tw-text-primary';
  }

  get qas(): QA[] {
    return this.persona === 'investor' ? INVESTOR_QAS : ISSUER_QAS;
  }

  /** The endpoint returns no description, so use a short generic line per lane. */
  cardDescription(item: KnowledgeBrowseItem): string {
    return item.type === 'learn' ? 'Learn how it works.' : 'Get a specific task done.';
  }

  /** Map the free-string productLine onto the card's known set; unknown → undefined (no pill). */
  toProductLine(value?: string): KcProductLine | undefined {
    const known: KcProductLine[] = ['ecf', 'pstx', 'ieo', 'general'];
    const v = (value || '').toLowerCase() as KcProductLine;
    return known.includes(v) ? v : undefined;
  }
}

// Curated, illustrative Q&A ported from the validated #46 prototype (INVESTOR_QAS / ISSUER_QAS).
// No AI backend yet (Phase 2): these are static, clearly-demo answers.
const INVESTOR_QAS: QA[] = [
  {
    chip: 'How ECF works',
    q: 'How does ECF investing work?',
    a:
      'You buy shares in early-stage Malaysian companies through pitchIN — a platform regulated by the ' +
      'Securities Commission. Browse a live campaign, invest online, and if it reaches its target you ' +
      'become a shareholder.',
    citations: [
      { num: 1, title: 'What is Equity Crowdfunding?' },
      { num: 2, title: 'How ECF works on pitchIN' },
    ],
  },
  {
    chip: 'Withdraw money',
    q: 'How do I withdraw my money?',
    a:
      'Go to Wallet → Withdraw. Your funds settle to your verified bank account via DuitNow, usually ' +
      'take up to 7 working days.',
    citations: [
      { num: 1, title: 'How withdrawals work' },
      { num: 2, title: 'Verifying your bank account' },
    ],
  },
  {
    chip: 'Verify my account',
    q: 'How do I verify my account?',
    a:
      'Upload your MyKad or passport and take a quick selfie. Most verifications clear within minutes — ' +
      'occasionally a manual review takes up to one business day.',
    citations: [
      { num: 1, title: 'How to verify your account (KYC)' },
      { num: 2, title: 'How long verification takes' },
    ],
  },
  {
    chip: 'What if a campaign fails?',
    q: "What if a campaign doesn't reach its target?",
    a:
      'You get your money back — in full. pitchIN works all-or-nothing: if a campaign misses its minimum ' +
      'target, your money returns to your pitchIN wallet, no shares are issued, and you’re charged nothing.',
    citations: [
      { num: 1, title: "What happens if a campaign doesn't reach its target?" },
      { num: 2, title: 'How refunds and wallet returns work' },
    ],
  },
];

const ISSUER_QAS: QA[] = [
  {
    chip: 'Am I eligible to raise?',
    q: 'Am I eligible to raise on pitchIN?',
    a:
      'Most Malaysian-incorporated companies (Sdn Bhd) can raise through ECF. Submit your company details ' +
      'and SSM documents, and our team reviews eligibility before you build a campaign.',
    citations: [
      { num: 1, title: 'Issuer eligibility' },
      { num: 2, title: 'How to onboard as an issuer' },
    ],
  },
  {
    chip: 'Prepare my campaign',
    q: 'How do I prepare my campaign?',
    a:
      'Structure your offer, prepare your pitch and financials, and set a realistic target. We guide you ' +
      'through each step before you go live.',
    citations: [
      { num: 1, title: 'Preparing your raise' },
      { num: 2, title: 'What a winning campaign looks like' },
    ],
  },
  {
    chip: 'Issuer fees',
    q: 'What fees does an issuer pay?',
    a:
      'pitchIN charges a success-based fee on the funds you raise — you pay nothing if the raise doesn’t ' +
      'complete. Fees are deducted before disbursement.',
    citations: [
      { num: 1, title: 'Issuer fees explained' },
      { num: 2, title: 'When funds are disbursed' },
    ],
  },
  {
    chip: 'When funds are disbursed',
    q: 'When are funds disbursed?',
    a:
      'After a successful raise, funds are disbursed once the cooling-off and settlement period closes — ' +
      'typically a few weeks after the campaign ends.',
    citations: [
      { num: 1, title: 'Disbursement timeline' },
      { num: 2, title: 'Settlement explained' },
    ],
  },
];
